import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import {
  Search, Plus, Trash2, FolderOpen, FileText, Eye, Download, Lock, Users,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { id: 'participation_agreements', name: 'Participation Agreements' },
  { id: 'cession_documents', name: 'Cession Documents' },
  { id: 'terms_conditions', name: 'Terms & Conditions' },
  { id: 'investor_mandate', name: 'Investor Mandate' },
  { id: 'tax_certificates', name: 'Tax Certificates' },
  { id: 'fica_documents', name: 'FICA Documents' },
  { id: 'correspondence', name: 'Correspondence' },
  { id: 'other', name: 'Other' },
];

function getCategoryLabel(id) {
  const found = CATEGORIES.find(function(c) { return c.id === id; });
  return found ? found.name : (id || '').replace(/_/g, ' ');
}

function formatFileSize(bytes) {
  if (!bytes) return '-';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / 1048576).toFixed(1) + ' MB';
}

function StatCard({ icon, label, value }) {
  return (
    <Card className="border border-gray-100">
      <CardContent className="p-4 flex items-center gap-3">
        <div className="p-2 bg-gray-50 rounded-lg">{icon}</div>
        <div>
          <p className="text-xs text-[#6C757D] uppercase tracking-wide">{label}</p>
          <p className="text-xl font-bold text-[#212529]">{value}</p>
        </div>
      </CardContent>
    </Card>
  );
}

function DetailRow({ label, value, mono }) {
  return (
    <div className="flex justify-between gap-4">
      <span className="text-[#6C757D] shrink-0">{label}</span>
      <span className={'text-[#212529] text-right ' + (mono ? 'font-mono text-xs' : '')}>{value}</span>
    </div>
  );
}

// Admin Documents Management Page - v3 clean rewrite
export default function AdminDocumentsPage() {
  const { token } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [investors, setInvestors] = useState([]);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [userFilter, setUserFilter] = useState('all');
  const [createOpen, setCreateOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [form, setForm] = useState({
    user_id: '', category: 'correspondence', document_name: '',
    file_type: 'pdf', description: '', is_confidential: false,
    loan_id: '', loan_reference: '',
  });

  var authHeaders = { Authorization: 'Bearer ' + token, 'Content-Type': 'application/json' };

  var fetchDocuments = useCallback(function() {
    setLoading(true);
    fetch(API_URL + '/api/admin/documents', { headers: { Authorization: 'Bearer ' + token } })
      .then(function(res) {
        if (!res.ok) throw new Error('Failed to fetch documents');
        return res.json();
      })
      .then(function(data) {
        setDocuments(Array.isArray(data) ? data : []);
      })
      .catch(function(err) { toast.error(err.message); })
      .finally(function() { setLoading(false); });
  }, [token]);

  var fetchInvestors = useCallback(function() {
    fetch(API_URL + '/api/admin/users?role=investor&limit=500', { headers: { Authorization: 'Bearer ' + token } })
      .then(function(res) { if (res.ok) return res.json(); throw new Error(); })
      .then(function(data) { setInvestors(data.users || []); })
      .catch(function() {});
  }, [token]);

  useEffect(function() { fetchDocuments(); fetchInvestors(); }, [fetchDocuments, fetchInvestors]);

  useEffect(function() {
    var timer = setTimeout(function() { setSearch(searchInput); }, 400);
    return function() { clearTimeout(timer); };
  }, [searchInput]);

  var filtered = documents.filter(function(d) {
    if (categoryFilter !== 'all' && d.category !== categoryFilter) return false;
    if (userFilter !== 'all' && d.user_id !== userFilter) return false;
    if (search) {
      var s = search.toLowerCase();
      var name = (d.document_name || '').toLowerCase();
      var userEmail = (d.user && d.user.email) ? d.user.email.toLowerCase() : '';
      var userName = ((d.user && d.user.first_name) || '') + ' ' + ((d.user && d.user.last_name) || '');
      if (!name.includes(s) && !userEmail.includes(s) && !userName.toLowerCase().includes(s)) return false;
    }
    return true;
  });

  var stats = {
    total: documents.length,
    categories: [...new Set(documents.map(function(d) { return d.category; }))].length,
    confidential: documents.filter(function(d) { return d.is_confidential; }).length,
    investors: [...new Set(documents.map(function(d) { return d.user_id; }))].length,
  };

  function doCreate() {
    if (!form.user_id || !form.document_name) {
      toast.error('User and document name are required');
      return;
    }
    fetch(API_URL + '/api/admin/documents', {
      method: 'POST', headers: authHeaders, body: JSON.stringify(form),
    })
      .then(function(res) { return res.json().then(function(data) { return { ok: res.ok, data: data }; }); })
      .then(function(result) {
        if (!result.ok) throw new Error(result.data.detail || 'Failed');
        toast.success('Document created');
        setCreateOpen(false);
        setForm({ user_id: '', category: 'correspondence', document_name: '', file_type: 'pdf', description: '', is_confidential: false, loan_id: '', loan_reference: '' });
        fetchDocuments();
      })
      .catch(function(err) { toast.error(err.message); });
  }

  function doDelete() {
    if (!selectedDoc) return;
    fetch(API_URL + '/api/admin/documents/' + selectedDoc.id, {
      method: 'DELETE', headers: { Authorization: 'Bearer ' + token },
    })
      .then(function(res) { return res.json().then(function(data) { return { ok: res.ok, data: data }; }); })
      .then(function(result) {
        if (!result.ok) throw new Error(result.data.detail || 'Failed');
        toast.success('Document deleted');
        setDeleteOpen(false);
        fetchDocuments();
      })
      .catch(function(err) { toast.error(err.message); });
  }

  function doDownload(doc) {
    fetch(API_URL + '/api/admin/documents/' + doc.id + '/download', {
      headers: { Authorization: 'Bearer ' + token },
    })
      .then(function(res) {
        if (!res.ok) throw new Error('Download failed');
        var ct = res.headers.get('content-type') || '';
        return res.blob().then(function(blob) { return { blob: blob, ct: ct }; });
      })
      .then(function(result) {
        var url = URL.createObjectURL(result.blob);
        if (result.ct.includes('pdf') || result.ct.includes('image') || result.ct.includes('text')) {
          window.open(url, '_blank');
        } else {
          var a = document.createElement('a');
          a.href = url;
          a.download = (doc.document_name || 'document') + '.' + (doc.file_type || 'pdf');
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
        }
        setTimeout(function() { URL.revokeObjectURL(url); }, 60000);
      })
      .catch(function(err) { toast.error(err.message); });
  }

  return (
    <div className="space-y-6" data-testid="admin-documents-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#212529] font-['Manrope']">Document Management</h1>
          <p className="text-sm text-[#6C757D] mt-1">Manage investor and client documents</p>
        </div>
        <Button onClick={function() { setCreateOpen(true); }} className="bg-[#1B4332] text-white hover:bg-[#143628]" data-testid="create-document-btn">
          <Plus className="h-4 w-4 mr-2" /> Add Document
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard icon={<FileText className="h-5 w-5 text-[#1B4332]" />} label="Total Documents" value={stats.total} />
        <StatCard icon={<FolderOpen className="h-5 w-5 text-blue-600" />} label="Categories Used" value={stats.categories} />
        <StatCard icon={<Lock className="h-5 w-5 text-red-600" />} label="Confidential" value={stats.confidential} />
        <StatCard icon={<Users className="h-5 w-5 text-emerald-600" />} label="Users with Docs" value={stats.investors} />
      </div>

      <Card className="border border-gray-100">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input placeholder="Search documents, users..." value={searchInput} onChange={function(e) { setSearchInput(e.target.value); }} className="pl-10" data-testid="doc-search-input" />
            </div>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-[200px]" data-testid="category-filter"><SelectValue placeholder="All Categories" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {CATEGORIES.map(function(c) { return <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>; })}
              </SelectContent>
            </Select>
            <Select value={userFilter} onValueChange={setUserFilter}>
              <SelectTrigger className="w-[200px]" data-testid="user-filter"><SelectValue placeholder="All Users" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Users</SelectItem>
                {investors.map(function(u) { return <SelectItem key={u.id} value={u.id}>{u.first_name} {u.last_name}</SelectItem>; })}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Card className="border border-gray-100">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="documents-table">
              <thead>
                <tr className="border-b bg-gray-50/80">
                  <th className="text-left px-4 py-3 font-medium text-[#6C757D]">Document</th>
                  <th className="text-left px-4 py-3 font-medium text-[#6C757D]">Category</th>
                  <th className="text-left px-4 py-3 font-medium text-[#6C757D]">User</th>
                  <th className="text-left px-4 py-3 font-medium text-[#6C757D]">Type</th>
                  <th className="text-left px-4 py-3 font-medium text-[#6C757D]">Size</th>
                  <th className="text-left px-4 py-3 font-medium text-[#6C757D]">Date</th>
                  <th className="text-right px-4 py-3 font-medium text-[#6C757D]">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={7} className="text-center py-12 text-gray-400">Loading...</td></tr>
                ) : filtered.length === 0 ? (
                  <tr><td colSpan={7} className="text-center py-12 text-gray-400">No documents found</td></tr>
                ) : filtered.map(function(doc) {
                  return (
                    <tr key={doc.id} className="border-b last:border-b-0 hover:bg-gray-50/60 transition-colors" data-testid={'doc-row-' + doc.id}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          {doc.is_confidential && <Lock className="h-3.5 w-3.5 text-red-500 flex-shrink-0" />}
                          <span className="font-medium text-[#212529] truncate max-w-[280px]">{doc.document_name}</span>
                        </div>
                        {doc.description && <p className="text-xs text-[#6C757D] mt-0.5 truncate max-w-[280px]">{doc.description}</p>}
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant="outline" className="text-xs bg-gray-50">{getCategoryLabel(doc.category)}</Badge>
                      </td>
                      <td className="px-4 py-3 text-[#6C757D]">
                        {doc.user ? (doc.user.first_name + ' ' + doc.user.last_name) : (doc.user_id || '').substring(0, 8)}
                      </td>
                      <td className="px-4 py-3 text-[#6C757D] uppercase text-xs font-mono">{doc.file_type || '-'}</td>
                      <td className="px-4 py-3 text-[#6C757D] text-xs">{formatFileSize(doc.file_size)}</td>
                      <td className="px-4 py-3 text-[#6C757D] text-xs">{doc.created_at ? new Date(doc.created_at).toLocaleDateString('en-ZA') : '-'}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="icon" onClick={function() { doDownload(doc); }} title="Download / View" data-testid={'download-doc-' + doc.id}>
                            <Download className="h-4 w-4 text-[#1B4332]" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={function() { setSelectedDoc(doc); setDetailOpen(true); }} title="Details" data-testid={'view-doc-' + doc.id}>
                            <Eye className="h-4 w-4 text-gray-500" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={function() { setSelectedDoc(doc); setDeleteOpen(true); }} title="Delete" data-testid={'delete-doc-' + doc.id}>
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {filtered.length > 0 && (
            <div className="px-4 py-3 border-t text-sm text-[#6C757D]">
              Showing {filtered.length} of {documents.length} documents
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-lg" data-testid="create-document-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Manrope']">Add Document</DialogTitle>
            <DialogDescription>Create a document record for an investor or client.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-2">
            <div>
              <Label>User *</Label>
              <Select value={form.user_id} onValueChange={function(v) { setForm(function(f) { return Object.assign({}, f, { user_id: v }); }); }}>
                <SelectTrigger data-testid="create-doc-user"><SelectValue placeholder="Select user..." /></SelectTrigger>
                <SelectContent>
                  {investors.map(function(u) { return <SelectItem key={u.id} value={u.id}>{u.first_name} {u.last_name} ({u.email})</SelectItem>; })}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Document Name *</Label><Input value={form.document_name} onChange={function(e) { setForm(function(f) { return Object.assign({}, f, { document_name: e.target.value }); }); }} data-testid="create-doc-name" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Category</Label>
                <Select value={form.category} onValueChange={function(v) { setForm(function(f) { return Object.assign({}, f, { category: v }); }); }}>
                  <SelectTrigger data-testid="create-doc-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(function(c) { return <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>; })}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>File Type</Label>
                <Select value={form.file_type} onValueChange={function(v) { setForm(function(f) { return Object.assign({}, f, { file_type: v }); }); }}>
                  <SelectTrigger data-testid="create-doc-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pdf">PDF</SelectItem>
                    <SelectItem value="docx">DOCX</SelectItem>
                    <SelectItem value="xlsx">XLSX</SelectItem>
                    <SelectItem value="csv">CSV</SelectItem>
                    <SelectItem value="png">PNG</SelectItem>
                    <SelectItem value="jpg">JPG</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Loan ID</Label><Input placeholder="Optional" value={form.loan_id} onChange={function(e) { setForm(function(f) { return Object.assign({}, f, { loan_id: e.target.value }); }); }} data-testid="create-doc-loan-id" /></div>
              <div><Label>Loan Reference</Label><Input placeholder="Optional" value={form.loan_reference} onChange={function(e) { setForm(function(f) { return Object.assign({}, f, { loan_reference: e.target.value }); }); }} data-testid="create-doc-loan-ref" /></div>
            </div>
            <div><Label>Description</Label><Textarea value={form.description} onChange={function(e) { setForm(function(f) { return Object.assign({}, f, { description: e.target.value }); }); }} rows={2} data-testid="create-doc-desc" /></div>
            <div className="flex items-center gap-2">
              <input type="checkbox" id="confidential" checked={form.is_confidential} onChange={function(e) { setForm(function(f) { return Object.assign({}, f, { is_confidential: e.target.checked }); }); }} className="rounded border-gray-300" data-testid="create-doc-confidential" />
              <Label htmlFor="confidential" className="text-sm cursor-pointer">Mark as confidential</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={function() { setCreateOpen(false); }}>Cancel</Button>
            <Button onClick={doCreate} className="bg-[#1B4332] text-white hover:bg-[#143628]" data-testid="confirm-create-doc">Create Document</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-md" data-testid="doc-detail-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Manrope']">Document Details</DialogTitle>
          </DialogHeader>
          {selectedDoc && (
            <div className="space-y-3 text-sm">
              <DetailRow label="Name" value={selectedDoc.document_name} />
              <DetailRow label="Category" value={getCategoryLabel(selectedDoc.category)} />
              <DetailRow label="User" value={selectedDoc.user ? (selectedDoc.user.first_name + ' ' + selectedDoc.user.last_name + ' (' + selectedDoc.user.email + ')') : selectedDoc.user_id} />
              <DetailRow label="File Type" value={selectedDoc.file_type ? selectedDoc.file_type.toUpperCase() : '-'} />
              <DetailRow label="File Size" value={formatFileSize(selectedDoc.file_size)} />
              <DetailRow label="Loan ID" value={selectedDoc.loan_id || '-'} />
              <DetailRow label="Loan Reference" value={selectedDoc.loan_reference || '-'} />
              <DetailRow label="Confidential" value={selectedDoc.is_confidential ? 'Yes' : 'No'} />
              <DetailRow label="Description" value={selectedDoc.description || '-'} />
              <DetailRow label="Created" value={selectedDoc.created_at ? new Date(selectedDoc.created_at).toLocaleString('en-ZA') : '-'} />
              <DetailRow label="ID" value={selectedDoc.id} mono />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={function() { setDetailOpen(false); }}>Close</Button>
            <Button onClick={function() { setDetailOpen(false); doDownload(selectedDoc); }} className="bg-[#1B4332] text-white hover:bg-[#143628]" data-testid="detail-download-btn">
              <Download className="h-4 w-4 mr-2" /> Download
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent data-testid="delete-doc-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Document</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{selectedDoc && selectedDoc.document_name}</strong>? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-doc">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

